/**
 * The guided tour's step model — pure data and index arithmetic, no DOM.
 *
 * The tour walks the build of ONE video, across screens (spec §2.6): New job
 * → Render queue → Library → the video page → the Studio → back to the page.
 * Every step names a `data-tour` anchor; the provider (`components/tour/`)
 * finds it at run time and draws the spotlight. A missing anchor is not an
 * error — the card just centres — so this file is the only place the order,
 * the copy and the anchor names live.
 *
 * Steps that need a rendered video (`requiresVideo`) are SKIPPED by the index
 * helpers when the app has none: a brand-new user gets the five-step tour
 * that ends at the Library, and the rest of the steps appear once they have
 * something to open.
 *
 * On phones the sidebar lives in a closed drawer and the Studio chat in a
 * closed one too, so their anchors are off screen until something opens them.
 * The owners hold them open for the step that points at them — the app shell
 * for `nav-*` (via the provider's `onStep`), the Studio for `studio-chat`
 * (via `useTour()`) — so every step gets its spotlight (2026-09-17; before
 * that the nav steps were an unlit bottom sheet).
 */

/** Where a step lives. `video` is the video page (a Library job open),
 *  `studio` the Studio editor for that job; the rest are sidebar routes. */
export type TourRoute = "new" | "queue" | "library-videos" | "video" | "studio";

export const TOUR_ROUTES: readonly TourRoute[] = ["new", "queue", "library-videos", "video", "studio"];

export type TourPlacement = "top" | "bottom" | "left" | "right" | "auto";

export interface TourStep {
  id: string;
  route: TourRoute;
  /** The `data-tour` value of the control this step points at. */
  anchor: string;
  title: string;
  /** One plain sentence, ≤ 12 words, no engine names. */
  body: string;
  placement: TourPlacement;
  /** Only meaningful once the app has a rendered video to open. */
  requiresVideo?: boolean;
}

export const TOUR_STEPS: readonly TourStep[] = [
  { id: "nav-new", route: "new", anchor: "nav-new", placement: "right",
    title: "Start here", body: "Every video begins on New job." },
  { id: "brand-chip", route: "new", anchor: "brand-chip", placement: "bottom",
    title: "Your brand", body: "Switch, edit or add a brand here." },
  { id: "new-sample", route: "new", anchor: "new-sample", placement: "auto",
    title: "Try it with our sample", body: "Paste a link, upload a file, or try the sample first." },
  { id: "new-start", route: "new", anchor: "new-start", placement: "auto",
    title: "Start the build", body: "One click. It takes a few minutes." },
  { id: "nav-queue", route: "queue", anchor: "nav-queue", placement: "right",
    title: "Watch it build", body: "Follow each step while your video builds." },
  { id: "nav-library", route: "library-videos", anchor: "nav-library", placement: "right",
    title: "Your videos", body: "Every finished video lands here, ready to open." },
  { id: "library-card", route: "library-videos", anchor: "library-card", placement: "auto", requiresVideo: true,
    title: "Open a video", body: "Click a card to see the video and what's next." },
  { id: "open-studio", route: "video", anchor: "open-studio", placement: "auto", requiresVideo: true,
    title: "Change anything", body: "Words, look, sound, camera — change any of it here." },
  { id: "studio-captions-tab", route: "studio", anchor: "studio-captions-tab", placement: "auto", requiresVideo: true,
    title: "Fix the words", body: "Click any caption to change what it says." },
  { id: "studio-chat", route: "studio", anchor: "studio-chat", placement: "auto", requiresVideo: true,
    title: "Or just say it", body: "Tell the editor what feels off." },
  { id: "studio-edit-clip", route: "studio", anchor: "studio-edit-clip", placement: "auto", requiresVideo: true,
    title: "Start, end, sound, camera, versions", body: "Cut or extend, swap the music, reframe, restore a version." },
  { id: "studio-test-render", route: "studio", anchor: "studio-test-render", placement: "auto", requiresVideo: true,
    title: "Try six seconds first", body: "A quick slice of the real render. Saves nothing." },
  { id: "studio-approve", route: "studio", anchor: "studio-approve", placement: "auto", requiresVideo: true,
    title: "Build the final video", body: "Happy with it? Build the finished file here." },
  { id: "schedule-post", route: "video", anchor: "schedule-post", placement: "auto", requiresVideo: true,
    title: "Post it", body: "Send it to your channels, right from here." },
];

/** The screen a step belongs on — what the provider compares to the app's
 *  current route to decide between a spotlight and "Take me there". */
export function stepRoute(step: TourStep): TourRoute {
  return step.route;
}

/** A step is shown only when what it points at can exist. */
export function isStepAvailable(step: TourStep, hasVideo: boolean): boolean {
  return !step.requiresVideo || hasVideo;
}

export function visibleSteps(steps: readonly TourStep[], hasVideo: boolean): TourStep[] {
  return steps.filter((s) => isStepAvailable(s, hasVideo));
}

/**
 * Index of the next available step after `i`, or `null` at the end.
 * `i` below zero means "before the first step"; anything past the end is
 * treated as the end.
 */
export function nextIndex(i: number, steps: readonly TourStep[], hasVideo: boolean): number | null {
  const from = Number.isFinite(i) ? Math.max(-1, Math.floor(i)) : -1;
  for (let j = from + 1; j < steps.length; j++) {
    if (isStepAvailable(steps[j], hasVideo)) return j;
  }
  return null;
}

/** Index of the previous available step before `i`, or `null` at the start. */
export function prevIndex(i: number, steps: readonly TourStep[], hasVideo: boolean): number | null {
  const from = Number.isFinite(i) ? Math.min(steps.length, Math.floor(i)) : steps.length;
  for (let j = from - 1; j >= 0; j--) {
    if (isStepAvailable(steps[j], hasVideo)) return j;
  }
  return null;
}

/** True when no available step follows `i` — the card reads "Done". */
export function finishedAfter(i: number, steps: readonly TourStep[], hasVideo: boolean): boolean {
  return nextIndex(i, steps, hasVideo) === null;
}

/**
 * "Step n of N" for the card, counted over the steps the user can actually
 * see. A step that is NOT available (resumed onto a video step after the
 * video went away) still gets a slot so the label never reads "0 of 5".
 */
export function positionOf(i: number, steps: readonly TourStep[], hasVideo: boolean): { n: number; total: number } {
  const visible = visibleSteps(steps, hasVideo);
  const step = steps[i];
  if (step && isStepAvailable(step, hasVideo)) {
    return { n: visible.indexOf(step) + 1, total: visible.length };
  }
  const before = steps.slice(0, Math.max(0, i)).filter((s) => isStepAvailable(s, hasVideo)).length;
  return { n: before + 1, total: visible.length + 1 };
}
